import {AbstractEditPage} from "../../../framework/entity/edit/edit-page/abstract-edit-page";
import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {EntityData, provideEntityModel} from "@fedeltapointofsale/data-client";
import {MixAndMatchEntityModel} from "../mix-and-match-entity.model";
import {debounceTime, Subject, Subscription} from "rxjs";
import {Config, Plugin} from "../../../config";
import {MixAndMatchComplexityAnalysisService} from "../mix-and-match-complexity-analysis.service";
import {
    Intersection,
    MixAndMatchComplexity,
    MixAndMatchComplexityAnalysis
} from "../mix-and-match-complexity-analysis.data";
import {TranslateService} from "@ngx-translate/core";
import {EditGridComponent} from "../../../framework/entity/edit/edit-grid/edit-grid.component";
import {
    ProductLineSearchPageComponent
} from "../../../catalogue/product-line/product-line-search-page/product-line-search-page.component";
import {
    CompositeProductEditPageComponent
} from "../../../catalogue/composite-product/composite-product-edit-page/composite-product-edit-page.component";
import {MixAndMatchInclusionData, MixAndMatchScheduleSummaryData, MixAndMatchType} from "../mix-and-match.data";
import {
    ProductLineEditPageComponent
} from "../../../catalogue/product-line/product-line-edit-page/product-line-edit-page.component";
import {
    CompositeProductSearchPageComponent
} from "../../../catalogue/composite-product/composite-product-search-page/composite-product-search-page.component";
import {MixAndMatchInclusionSearchData} from "../mix-and-match-inclusion-search.data";
import {EditPageComponent} from "../../../framework/entity/edit/edit-page/edit-page.component";
import {
    ProductLineCustomObjectEditPageComponent
} from "../../../custom-objects/custom-object/custom-object-edit-page/product-line-custom-object-edit-page.component";
import {
    ProductLineCustomObjectSearchPageComponent
} from "../../../custom-objects/custom-object/custom-object-search-page/product-line-custom-object-search-page.component";
import {FormGroup} from "@angular/forms";
import {EditMode} from "../../../framework/entity/edit/edit-page/edit-mode";
import {
    MixAndMatchScheduleEditPageComponent
} from "../../mix-and-match-schedule/mix-and-match-schedule-edit-page/mix-and-match-schedule-edit-page.component"
import {PricingScheduleSummaryData} from "../../pricing-schedule/pricing-schedule.data";
import {
    FUIAlertAction,
    FUIAlertActions,
    FUIAlertService,
    FUIResponsiveService,
    FUISpinnersService
} from "@fedeltapointofsale/ui";
import {
    DiscontinueSearchActionComponent
} from "../../../framework/common-controls/actions/discontinue-search-action.component";
import {
    MixAndMatchScheduleDiscontinueAction,
    MixAndMatchScheduleDiscontinueService
} from "../mix-and-match-schedule-discontinue.service";

@Component({
    selector: "wbo-mix-and-match-edit-page",
    styleUrls: ["./mix-and-match-edit-page.component.scss"],
    templateUrl: "./mix-and-match-edit-page.component.html",
    providers: [
        provideEntityModel(MixAndMatchEntityModel),
        MixAndMatchComplexityAnalysisService,
        MixAndMatchScheduleDiscontinueService
    ]
})
export class MixAndMatchEditPageComponent extends AbstractEditPage implements OnInit, OnDestroy {
    Plugin = Plugin;
    Config = Config;
    EditMode = EditMode;
    ProductLineSearchPageComponent = ProductLineSearchPageComponent;
    ProductLineEditPageComponent = ProductLineEditPageComponent;
    CompositeProductSearchPageComponent = CompositeProductSearchPageComponent;
    CompositeProductEditPageComponent = CompositeProductEditPageComponent;
    ProductLineCustomObjectSearchPageComponent = ProductLineCustomObjectSearchPageComponent;
    ProductLineCustomObjectEditPageComponent = ProductLineCustomObjectEditPageComponent;
    MixAndMatchComplexity = MixAndMatchComplexity;
    MixAndMatchScheduleEditPageComponent = MixAndMatchScheduleEditPageComponent;
    PricingScheduleSummaryData = PricingScheduleSummaryData;

    @ViewChild("inclusionsGrid") inclusionsGrid: EditGridComponent;
    @ViewChild(EditPageComponent) editPage: EditPageComponent;
    public complexityAnalysis: MixAndMatchComplexityAnalysis = <MixAndMatchComplexityAnalysis>EntityData.create(MixAndMatchComplexityAnalysis);
    public intersectionMessages: string[] = [];
    public MixAndMatchType = MixAndMatchType;
    private subscriptions: Subscription = new Subscription();
    private productsChangeSubscription: Subscription;
    private analyseComplexity$: Subject<void> = new Subject<void>();

    constructor(public model: MixAndMatchEntityModel,
                private complexityAnalysisService: MixAndMatchComplexityAnalysisService,
                private translateService: TranslateService,
                changeDetectorRef: ChangeDetectorRef,
                responsiveService: FUIResponsiveService,
                private alertService: FUIAlertService,
                private spinnersService: FUISpinnersService,
                private mixAndMatchScheduleDiscontinueService: MixAndMatchScheduleDiscontinueService) {
        super(changeDetectorRef, responsiveService);
    }

    get applyToAllProducts(): boolean {
        // applyToAllProducts may be set explicitly by the user (stored as a transient in the entity data for validation).
        // Otherwise, it is implicitly true for an existing mix-and-match if no products have been added.
        return this.model.data.applyToAllProducts === true || (this.model.data.applyToAllProducts === undefined && this.model.data.id !== undefined && this.model.data.inclusions.length === 0);
    }

    set applyToAllProducts(applyToAllProducts: boolean) {
        this.model.data.applyToAllProducts = applyToAllProducts;
        this.editPage.editService.dirty = true;
    }

    ngOnInit(): void {
        this.subscriptions.add(this.analyseComplexity$.pipe(debounceTime(1000)).subscribe(() => this.analyseComplexity()));

        this.subscriptions.add(this.model.$data.subscribe(() => {
            this.productsChangeSubscription = this.model.$collectionChanged("inclusions").subscribe(() => this.analyseComplexity$.next());

            // cloning a mix-and-match with no product lines - set applyToAllProducts
            if (this.model.data.name !== undefined && this.model.data.id === undefined && this.model.data.inclusions.length === 0) {
                this.model.data.applyToAllProducts = true;
            }
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        if (this.productsChangeSubscription) {
            this.productsChangeSubscription.unsubscribe();
        }
    }

    /**
     * Price-level and Amount validations are dependent upon Type. If type changes, force re-validation of price-level and amount.
     * @param event
     */
    typeSelected(event: any) {
        this.editPage.formGroup.get("priceLevel").get("priceLevelId").patchValue(this.model.data.priceLevel.priceLevelId);
        this.editPage.formGroup.get("amount").patchValue(this.model.data.amount);
    }

    mixAndMatchSaving(): void {
        if (this.applyToAllProducts) {
            this.model.data.inclusions = [];
        }
    }

    productsEditFormClosed(confirmed: boolean) {
        if (confirmed) {
            this.analyseComplexity$.next();
        }
    }

    minProductQtyChanged({value, dataItem, formGroup}: { value: any, dataItem: any, formGroup: FormGroup }): void {
        dataItem.minProductQty = value;
        if (dataItem.minProductQty != undefined && dataItem.maxProductQty != undefined && dataItem.maxProductQty < dataItem.minProductQty) {
            dataItem.maxProductQty = dataItem.minProductQty;
            formGroup.get("maxProductQty").setValue(dataItem.maxProductQty);
        }
    }

    maxProductQtyChanged({value, dataItem, formGroup}: { value: any, dataItem: any, formGroup: FormGroup }): void {
        dataItem.maxProductQty = value;
        if (dataItem.maxProductQty != undefined && dataItem.minProductQty != undefined && dataItem.minProductQty > dataItem.maxProductQty) {
            dataItem.minProductQty = dataItem.maxProductQty;
            formGroup.get("minProductQty").setValue(dataItem.minProductQty);
        }
    }

    extraOrCustomObjectChanged({
                                   value,
                                   dataItem,
                                   formGroup,
                                   fieldName
                               }: { value: any, dataItem: any, formGroup: FormGroup, fieldName: string }): void {
        if (fieldName === "extra") {
            dataItem.extraId = value;
        } else {
            dataItem.extraCustomObjectId = value;
        }
        this.analyseComplexity$.next();
    }

    analyseComplexity(): void {
        if (this.model.data.inclusions.length === 0) {
            this.intersectionMessages = [];
            return;
        }
        this.complexityAnalysisService.performAction(this.model.data)
            .then((result: any) => {
                this.complexityAnalysis.assign(result);

                this.intersectionMessages = [];
                this.model.data.inclusions.forEach((product: MixAndMatchInclusionData) => product.intersectionComplexity = MixAndMatchComplexity.SIMPLE);
                this.complexityAnalysis.intersections.forEach((intersection: Intersection) => {
                    this.model.data.inclusions.forEach((product: MixAndMatchInclusionData) => {
                        if (intersection.inclusion1.matches(product) ||
                            intersection.inclusion2.matches(product)) {
                            product.intersectionComplexity = this.complexityAnalysis.complexity;
                        }
                    });

                    let name1: string = intersection.inclusion1.customObjectId ? intersection.inclusion1.customObjectName : intersection.inclusion1.sellableName;
                    if (intersection.inclusion1.includeCompositeComponents) {
                        name1 = this.translateService.instant("wbo.mix-and-match.edit.message.plusComposites", {"name": name1});
                    }
                    let name2: string = intersection.inclusion2.customObjectId ? intersection.inclusion2.customObjectName : intersection.inclusion2.sellableName;
                    if (intersection.inclusion2.includeCompositeComponents) {
                        name2 = this.translateService.instant("wbo.mix-and-match.edit.message.plusComposites", {"name": name2});
                    }
                    this.intersectionMessages.push(this.translateService.instant("wbo.mix-and-match.edit.message.overlaps", {
                        "name1": name1,
                        "name2": name2
                    }));
                })
            });
    }

    addFromInclusionSearchData(searchData: MixAndMatchInclusionSearchData): void {
        const inclusion: MixAndMatchInclusionData = <MixAndMatchInclusionData>EntityData.create(MixAndMatchInclusionData);

        // The following will perform a deep copy of the selected data (the search results are preserved)
        if (searchData.sellable.sellableId) {
            inclusion.sellable = searchData.sellable;
        } else {
            inclusion.customObject = searchData.customObject;
        }

        this.inclusionsGrid.addAndEditRow(inclusion);
    }

    addFromSellable(data: any[]): void {
        let first: boolean = true;
        data.forEach((item) => {
            const inclusion: MixAndMatchInclusionData = <MixAndMatchInclusionData>EntityData.create(MixAndMatchInclusionData);
            inclusion.sellable = item;
            const index: number = this.inclusionsGrid.addRow(inclusion) - 1;
            if (first) {
                setTimeout(() => this.inclusionsGrid.editRow(inclusion, index, true));
                first = false;
            }
        });
    }

    addFromCustomProductGroup(data: any[]): void {
        let first: boolean = true;
        data.forEach((item) => {
            const inclusion: MixAndMatchInclusionData = <MixAndMatchInclusionData>EntityData.create(MixAndMatchInclusionData);
            inclusion.customObject = item;
            const index: number = this.inclusionsGrid.addRow(inclusion) - 1;
            if (first) {
                setTimeout(() => this.inclusionsGrid.editRow(inclusion, index, true));
                first = false;
            }
        });
    }

    async discontinueSchedule(mixAndMatchScheduleSummary: MixAndMatchScheduleSummaryData): Promise<void> {
        const action: FUIAlertAction = await this.alertService.showConfirmationOkCancel(DiscontinueSearchActionComponent.title, DiscontinueSearchActionComponent.message)
            .toPromise();
        if (action === FUIAlertActions.OK_ACTION) {
            this.spinnersService.open(true);
            try {
                await this.mixAndMatchScheduleDiscontinueService.performAction(new MixAndMatchScheduleDiscontinueAction(mixAndMatchScheduleSummary.mixAndMatchScheduleId));
                mixAndMatchScheduleSummary.discontinued = true;
            } finally {
                this.spinnersService.close();
            }
        }
    }

    scheduleRowRemovable(rowIndex: number, mixAndMatchScheduleSummary: MixAndMatchScheduleSummaryData): boolean {
        return !mixAndMatchScheduleSummary.discontinued;
    }

    discountItemChanged(): void {
        if (!this.model.data.discountItem) {
            this.model.data.discountExtras = true;
        }
    }

    discountExtrasChanged(): void {
        if (!this.model.data.discountExtras) {
            this.model.data.discountItem = true;
        }
    }
}
